import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../layouts/user';
import UserHead from '../../components/user/head';
import { Errors } from '../../components/common/msg';
import { get } from '../../libs/api';
import { isOkResponse, responseData } from '../../libs/apiResponse';
import {
  bondStatus,
  couponNominal,
  daysBetween,
  floatingFrom,
  frequencyName,
  isZeroCoupon,
  issuerInitials,
  lifeElapsedPercent,
  nextCoupon,
  orDash,
  priceSourceName,
  sectorName,
  showBondAmount,
  showBondDate,
  showParPrice,
  showRate,
  todayIso,
  upcomingEvents,
  yearsToMaturity,
} from '../../libs/bond';
import './bond.css';

// Per-bond master data page (PB-2339). Data comes from backend-api via
// auth-service; every bond is shown, whether or not the client holds it.

const STALE_PRICE_DAYS = 7;
const EVENTS_SHOWN = 6;
const SOURCES_SHOWN = 4;

const STATUS_TONES = {
  ACTIVE: 'success',
  MATURED: 'secondary',
  PERPETUAL: 'info',
};

const EVENT_DOTS = {
  coupon: 'bg-primary',
  redemption: 'bg-success',
  option: 'bg-warning',
};

const OPTION_HOLDERS = {
  CALL: 'Issuer call',
  PUT: 'Holder put',
};

const optionPrice = (c) =>
  c.redemptionPrice != null
    ? showParPrice(c.redemptionPrice)
    : c.benchmarkSpreadBp != null
      ? `Make-whole T+${c.benchmarkSpreadBp}bp`
      : '-';

const eventText = (event, bond) => {
  if (event.kind === 'option') {
    const c = event.option;
    const who = OPTION_HOLDERS[c.direction] || orDash(c.direction);
    return {
      title:
        c.redemptionPrice != null
          ? `${who} at ${showParPrice(c.redemptionPrice)}`
          : `${who} window (${optionPrice(c)})${
              c.untilDate ? ` until ${showBondDate(c.untilDate)}` : ''
            }`,
      detail: orDash(c.optionStyle),
    };
  }
  const f = event.flow;
  if (event.kind === 'redemption') {
    return {
      title: f.derived ? 'Redemption at par' : 'Redemption',
      detail: showBondAmount(f.couponAmount, bond.currency),
    };
  }
  return {
    title: f.isFloating ? 'Floating coupon' : `Coupon ${showRate(f.couponRate)}`,
    detail: f.isFloating ? 'Rate set later' : showBondAmount(f.couponAmount, bond.currency),
  };
};

const Fact = ({ label, children }) => (
  <div className="d-flex justify-content-between py-2 border-bottom">
    <span className="sub-text">{label}</span>
    <span className="lead-text text-right ml-3">{children}</span>
  </div>
);

const Stat = ({ label, value, meta }) => (
  <div className="col-sm-6 col-xl-3 mb-3 mb-xl-0">
    <div className="sub-text">{label}</div>
    <div className="lead-text bond-stat">{value}</div>
    <div className="sub-text">{meta}</div>
  </div>
);

const Hero = ({ bond, status, price, priceAge }) => {
  const accrued = bond.accruedInterest;
  const stale = priceAge > STALE_PRICE_DAYS;
  return (
    <div className="card card-bordered bond-hero mb-4">
      <div className="card-inner">
        <div className="d-flex flex-wrap align-items-center justify-content-between g-3">
          <div className="d-flex align-items-center">
            <div className="user-avatar lg bg-primary-dim mr-3">
              <span>{issuerInitials(bond.issuer || bond.name)}</span>
            </div>
            <div>
              <div className="sub-text">
                {[bond.issuer, sectorName(bond.sector), bond.country]
                  .filter((v) => v && v !== '-')
                  .join(' · ')}
              </div>
              <h4 className="mb-1">{bond.name}</h4>
              <span className="badge badge-dim badge-gray bond-isin mr-1">
                {orDash(bond.isin)}
              </span>
              {bond.cusip && (
                <span className="badge badge-dim badge-gray bond-isin mr-1">{bond.cusip}</span>
              )}
              <span className={`badge badge-${STATUS_TONES[status]}`}>{status}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="sub-text">Indicative price</div>
            <div className={`amount bond-hero-price ${stale ? 'text-warning' : ''}`}>
              {price ? showParPrice(price.price) : '-'}
            </div>
            <div className="sub-text">
              {price
                ? [
                    '% of par',
                    price.yield != null &&
                      `${price.yieldBasis || 'Yield'} ${showRate(price.yield, 2)}`,
                    priceSourceName(price),
                    showBondDate(price.priceDate),
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : 'No indicative price available'}
            </div>
            {stale && <div className="sub-text text-warning">Price is {priceAge} days old</div>}
            {accrued && (
              <div className="sub-text">
                Accrued interest {showBondAmount(accrued.amount, bond.currency)} per{' '}
                {showBondAmount(accrued.nominal, bond.currency)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Income = ({ bond, today }) => {
  const zero = isZeroCoupon(bond);
  const next = nextCoupon(bond, today);
  const floating = floatingFrom(bond);
  const nominal = couponNominal(bond);
  const accrued = bond.accruedInterest;
  const frequency = frequencyName(bond.couponFrequency);
  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner-group">
        <div className="card-inner bond-card-head">
          <h6 className="title mb-0">Income</h6>
        </div>
        <div className="card-inner">
          <div className="row">
            <Stat
              label="Coupon"
              value={zero ? 'Zero coupon' : showRate(bond.couponRate)}
              meta={
                zero
                  ? 'Pays par at maturity'
                  : floating
                    ? `${frequency ? `${frequency}, ` : ''}fixed until ${showBondDate(floating)}`
                    : frequency || '-'
              }
            />
            <Stat
              label="Next payment"
              value={zero ? 'None' : next ? showBondDate(next.paymentDate) : '-'}
              meta={
                zero
                  ? 'Zero-coupon bond'
                  : next
                    ? `in ${daysBetween(today, next.paymentDate)} days`
                    : 'None scheduled'
              }
            />
            <Stat
              label={`Per ${showBondAmount(nominal, bond.currency)} nominal`}
              value={
                next && !next.isFloating ? showBondAmount(next.couponAmount, bond.currency) : '-'
              }
              meta={next?.isFloating ? 'Floating, rate set later' : 'Next coupon amount'}
            />
            <Stat
              label="Accrued interest"
              value={accrued ? showBondAmount(accrued.amount, bond.currency) : '-'}
              meta={
                accrued
                  ? `${accrued.accruedDays} days, ${accrued.dayCount}`
                  : zero
                    ? 'None: zero-coupon bond'
                    : 'Not available for the current period'
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const WhatNext = ({ bond, today }) => {
  const [showAll, setShowAll] = useState(false);
  const events = upcomingEvents(bond, today);
  const shown = showAll ? events : events.slice(0, EVENTS_SHOWN);
  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner border-bottom bond-card-head">
        <div className="card-title-group">
          <div className="card-title">
            <h6 className="title">What happens next</h6>
          </div>
          <div className="card-tools sub-text">{events.length} upcoming</div>
        </div>
      </div>
      <div className="card-inner">
        {shown.length === 0 && <p className="sub-text mb-0">Nothing further scheduled.</p>}
        {shown.map((e, i) => {
          const text = eventText(e, bond);
          return (
            <div key={`${e.kind}-${e.date}-${i}`} className="d-flex">
              <div className="d-flex flex-column align-items-center mr-3">
                <span className={`${EVENT_DOTS[e.kind]} rounded-circle bond-timeline-dot`} />
                {i < shown.length - 1 && <span className="bg-lighter bond-timeline-rail" />}
              </div>
              <div className="d-flex justify-content-between flex-grow-1 pb-3">
                <div>
                  <div className="lead-text">{text.title}</div>
                  <div className="sub-text">{showBondDate(e.date)}</div>
                </div>
                <div className="sub-text text-right ml-3">{text.detail}</div>
              </div>
            </div>
          );
        })}
        {events.length > EVENTS_SHOWN && (
          <button
            type="button"
            className="btn btn-sm btn-light btn-white"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Show fewer' : `Show all ${events.length}`}
          </button>
        )}
        {events.some((e) => e.flow?.derived) && (
          <p className="sub-text mt-3 mb-0">The redemption is derived from the maturity date.</p>
        )}
      </div>
    </div>
  );
};

const CallPut = ({ bond, today }) => {
  if (bond.callPutSchedule.length === 0) return null;
  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner border-bottom bond-card-head">
        <h6 className="title mb-0">Call &amp; put</h6>
      </div>
      <table className="table table-ulogs mb-0">
        <thead>
          <tr>
            <th>Type</th>
            <th>Style</th>
            <th>From</th>
            <th>Until</th>
            <th>Price</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {bond.callPutSchedule.map((c) => {
            const inEffect = c.effectiveDate <= today && (!c.untilDate || c.untilDate >= today);
            return (
              <tr key={`${c.direction}-${c.effectiveDate}`}>
                <td>{OPTION_HOLDERS[c.direction] || orDash(c.direction)}</td>
                <td>{orDash(c.optionStyle)}</td>
                <td>{showBondDate(c.effectiveDate)}</td>
                <td>{showBondDate(c.untilDate)}</td>
                <td>{optionPrice(c)}</td>
                <td>
                  {inEffect ? (
                    <span className="badge badge-danger">IN EFFECT</span>
                  ) : c.effectiveDate > today ? (
                    'Upcoming'
                  ) : (
                    'Expired'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SidePanel = ({ bond, status, today }) => {
  const [allSources, setAllSources] = useState(false);
  const elapsed = lifeElapsedPercent(bond, today);
  const years = yearsToMaturity(bond, today);
  const shown = bond.indicativePrice;
  const isShown = (q) =>
    shown &&
    q.status !== 'REFERENCE' &&
    q.source === shown.source &&
    q.venueCode === shown.venueCode &&
    q.priceDate === shown.priceDate;
  const sources = allSources ? bond.priceSources : bond.priceSources.slice(0, SOURCES_SHOWN);
  return (
    <div className="card card-bordered mb-4 bond-sticky">
      <div className="card-inner-group">
        <div className="card-inner">
          <h6 className="title mb-3">Life of the bond</h6>
          {elapsed != null ? (
            <>
              <div className="progress progress-md bond-life-track">
                <div className="progress-bar" style={{ width: `${elapsed}%` }} />
              </div>
              <div className="d-flex justify-content-between sub-text mt-1">
                <span>{showBondDate(bond.issueDate)}</span>
                <span>{showBondDate(bond.maturityDate)}</span>
              </div>
              <div className="sub-text mt-1">
                {status === 'MATURED'
                  ? `Matured on ${showBondDate(bond.maturityDate)}`
                  : `${years.toFixed(1)} years to maturity`}
              </div>
            </>
          ) : (
            <div className="sub-text">Perpetual: no final maturity date.</div>
          )}
        </div>
        <div className="card-inner">
          <h6 className="title mb-2">Key facts</h6>
          <Fact label="Currency">{orDash(bond.currency)}</Fact>
          <Fact label="Seniority">{orDash(bond.seniority)}</Fact>
          <Fact label="Day count">{orDash(bond.dayCount)}</Fact>
          <Fact label="Issue date">{showBondDate(bond.issueDate)}</Fact>
          <Fact label="Maturity date">
            {bond.maturityDate ? showBondDate(bond.maturityDate) : 'Perpetual'}
          </Fact>
          <Fact label="Min. denomination">{showBondAmount(bond.parValue, bond.currency)}</Fact>
          <Fact label="Callable">{bond.callable ? 'Yes' : 'No'}</Fact>
        </div>
        <div className="card-inner">
          <h6 className="title mb-2">Price sources</h6>
          {bond.priceSources.length === 0 && <div className="sub-text">No prices received.</div>}
          {sources.map((q) => (
            <div
              key={`${q.source}-${q.venueCode}-${q.priceDate}`}
              className="d-flex justify-content-between py-1"
            >
              <span className="sub-text">
                {priceSourceName(q)}
                {isShown(q) && <span className="badge badge-dim badge-success ml-1">shown</span>}
              </span>
              <span className="lead-text">{showParPrice(q.price)}</span>
            </div>
          ))}
          {bond.priceSources.length > SOURCES_SHOWN && (
            <button
              type="button"
              className="btn btn-sm btn-light btn-white mt-2"
              onClick={() => setAllSources(!allSources)}
            >
              {allSources ? 'Show fewer' : `Show all ${bond.priceSources.length}`}
            </button>
          )}
          {bond.priceVenueName ? (
            <p className="sub-text mt-2 mb-0">
              Price venue set by operations: {bond.priceVenueName}. Only this venue&apos;s price is
              shown.
            </p>
          ) : (
            <p className="sub-text mt-2 mb-0">
              One price is shown, in this order of preference: Cbonds Estimation, another Cbonds
              venue, then the custodian or DBS feed.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const Component = () => {
  const { securitymasterId } = useParams();
  const [bond, setBond] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const today = todayIso();

  useEffect(() => {
    // Ignore a slower response for a bond the user has already navigated away from.
    let current = true;
    const notFound = () => {
      if (current) setErrors(['This bond could not be found.']);
    };
    const load = async () => {
      setBond(null);
      setLoading(true);
      setErrors([]);
      try {
        const resp = await get('/api/bond/' + encodeURIComponent(securitymasterId));
        const data = responseData(resp);
        if (!isOkResponse(resp) || !data) {
          notFound();
          return;
        }
        if (current) setBond(data);
      } catch (e) {
        notFound();
      } finally {
        if (current) setLoading(false);
      }
    };
    load();
    return () => {
      current = false;
    };
  }, [securitymasterId]);

  const status = bond ? bondStatus(bond, today) : null;
  const price = bond?.indicativePrice;
  const priceAge = price ? daysBetween(price.priceDate, today) : null;

  return (
    <Layout menu="bonds">
      <UserHead sub="Bond Master" title="Bond Details" showBack={true} backTo="/portal/bonds" />

      <Errors errors={errors} />

      {!loading && bond && (
        <div className="bond-page">
          <Hero bond={bond} status={status} price={price} priceAge={priceAge} />
          <div className="row g-gs">
            <div className="col-lg-8">
              <Income bond={bond} today={today} />
              <WhatNext bond={bond} today={today} />
              <CallPut bond={bond} today={today} />
            </div>
            <div className="col-lg-4">
              <SidePanel bond={bond} status={status} today={today} />
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Component;
